import { lazy, Suspense, useCallback, useRef, useState } from 'react'

import { clsx } from 'clsx'
import { domToReact, HTMLReactParserOptions } from 'html-react-parser'

import { useRouter } from '@tribeplatform/react-sdk'
import { useAuthToken, useSpaceUtil } from '@tribeplatform/react-sdk/hooks'

import { getDomainInfo } from '../common/utils/domain.js'
import { ImageMarkupVersion } from '../Composer/extensions/image/types.js'
import type { Slide } from '../MediaModal/index.js'
import { Parser } from '../utils/html-react-parser.js'
import { ComposerModuleName, HtmlContentProps } from './@types.js'
import { AttachmentRenderer } from './AttachmentRenderer.js'
import { EmbedRenderer } from './EmbedRenderer.js'
import { DomAudio, renderV2Img, DomVideo, renderV1Img } from './renderers.js'
import { figureToImage, imgToImage } from './utils.js'

const CodeBlock = lazy(() =>
  import('../CodeBlock/CodeBlock.js').then(m => ({ default: m.CodeBlock })),
)

const MediaModal = lazy(() =>
  import('../MediaModal/MediaModal.js').then(m => ({ default: m.MediaModal })),
)

export const HtmlContent = ({
  value,
  embeds,
  attachments,
  mentions,
  context,
  trustedDomains = [],
}: HtmlContentProps) => {
  const { generateMemberPath } = useSpaceUtil()
  const { Link } = useRouter()
  const {
    data: { networkPublicInfo },
  } = useAuthToken()
  const { domain, subfolder } = getDomainInfo(networkPublicInfo)

  if (trustedDomains?.indexOf(networkPublicInfo?.domain) === -1) {
    trustedDomains.push(networkPublicInfo?.domain)
  }
  if (subfolder && trustedDomains?.indexOf(domain) === -1) {
    trustedDomains.push(domain)
  }

  let mediaIndex = -1
  // The first one is active by default.
  const [activeMediaIndex, setActiveMediaIndex] = useState(0)
  const slides = useRef([])
  const [mediaModalVisibility, setMediaModalVisibility] = useState(false)

  const handleModalVisibility = useCallback(e => {
    setActiveMediaIndex(Number(e?.currentTarget.dataset?.index))
    setMediaModalVisibility(true)
  }, [])
  const handleModalClose = useCallback(() => {
    setMediaModalVisibility(false)
  }, [])

  const pushSlide = useCallback((newSlide: Slide) => {
    slides.current = [...slides.current, newSlide]
  }, [])

  if (!value) {
    return null
  }

  const options: HTMLReactParserOptions = {
    replace: _domNode => {
      const domNode = _domNode as any
      const tagName = domNode.name as string
      let isTrusted: boolean

      switch (tagName) {
        case 'figure': {
          switch (domNode.firstChild?.name) {
            case 'img': {
              const imgSlide = figureToImage(domNode)
              if (
                imgSlide &&
                !slides.current.find(slide => slide.id === imgSlide.id)
              ) {
                pushSlide(imgSlide)
              }

              mediaIndex += 1
              if (
                domNode?.attribs?.['data-version'] === ImageMarkupVersion.V2
              ) {
                return renderV2Img(domNode, {
                  mediaIndex,
                  onClick: handleModalVisibility,
                })
              }

              return renderV1Img(domNode.firstChild, {
                mediaIndex,
                onClick: handleModalVisibility,
                context,
              })
            }
            case 'audio':
              return <DomAudio domNode={domNode.firstChild} />
            case 'video':
              return <DomVideo domNode={domNode.firstChild} />
            default:
              return domNode
          }
        }
        case 'img': {
          const imgSlide = imgToImage(domNode)
          if (
            imgSlide &&
            !slides.current.find(slide => slide.id === imgSlide.id)
          ) {
            pushSlide(imgSlide)
          }
          mediaIndex += 1
          return renderV1Img(domNode, {
            mediaIndex,
            onClick: handleModalVisibility,
            context,
          })
        }
        case 'h1':
        case 'h2':
          return (
            <h2 className="text-xl">
              {domToReact(domNode?.children, options)}
            </h2>
          )

        case 'audio':
          return <DomAudio domNode={domNode} />
        case 'video':
          return <DomVideo domNode={domNode} />

        case 'code':
          return (
            <code className="bg-surface-pressed text-content rounded px-1.5 py-0.5">
              {domToReact(domNode?.children, options)}
            </code>
          )
        case 'pre': {
          if (
            !domNode?.children?.length ||
            domNode?.children[0]?.name !== 'code' ||
            domNode?.children[0]?.children?.length !== 1 ||
            domNode?.children[0]?.children[0]?.type !== 'text'
          ) {
            return null
          }

          const detectedLanguage =
            domNode?.children[0]?.attribs?.class?.replace('language-', '')
          const content = domToReact(domNode?.children[0].children)

          return (
            <div className={clsx('code-wrapper relative')}>
              <Suspense
                fallback={
                  <pre>
                    <code>{content}</code>
                  </pre>
                }
              >
                <CodeBlock
                  language={detectedLanguage}
                  content={content}
                  copyButton
                />
              </Suspense>
            </div>
          )
        }
        case 'h3':
          return (
            <h2 className="text-lg">
              {domToReact(domNode?.children, options)}
            </h2>
          )

        case 'ol':
          return (
            <ol
              start={domNode?.attribs?.start}
              className="list-decimal list-inside ps-6"
            >
              {domToReact(domNode?.children, options)}
            </ol>
          )

        case 'ul':
          return (
            <ul className="list-disc list-inside ps-6">
              {domToReact(domNode?.children, options)}
            </ul>
          )

        case 'div':
          if (
            domNode.type === 'tag' &&
            domNode.attribs?.['data-type'] === ComposerModuleName.Embed
          ) {
            return (
              <EmbedRenderer
                domNode={domNode}
                embeds={embeds}
                context={context}
              />
            )
          }
          break

        case 'attachment':
          if (
            domNode.type === 'tag' &&
            domNode.attribs?.['data-type'] === ComposerModuleName.Attachment
          ) {
            return (
              <AttachmentRenderer
                domNode={domNode}
                attachments={attachments}
                slides={slides.current}
                pushSlide={pushSlide}
                onClick={handleModalVisibility}
              />
            )
          }
          break

        case 'a':
          if (domNode.attribs?.['data-type'] === ComposerModuleName.Mention) {
            const mentionId = domNode.attribs['data-id']
            const mentionFromDb = mentions?.find?.(
              mention => mention.id === mentionId,
            )

            let str = mentionFromDb?.name
              ? `${mentionFromDb.name}`
              : domToReact(domNode?.children, options)?.toString?.()

            if (str?.[0] === '@') {
              str = str.replace('@', '')
            }
            return (
              <Link
                variant="accent"
                href={generateMemberPath({ id: mentionId })}
                trusted
              >
                {str}
              </Link>
            )
          }
          isTrusted = !!trustedDomains?.find(
            trustedDomain =>
              trustedDomain === '*' ||
              domNode?.attribs?.href?.indexOf(`https://${trustedDomain}`) === 0,
          )

          return (
            <Link
              variant="accent"
              href={domNode?.attribs?.href}
              // If isTrusted is false, let the Link component decide based on isInternal
              trusted={isTrusted || undefined}
            >
              {domToReact(domNode?.children, options)}
            </Link>
          )

        case 'iframe':
        case 'script':
          return <></>
        default:
          // If null is returned parser will render the html variant.
          return null
      }

      return null
    },
  }

  const Component = Parser(value, options)
  return (
    <>
      {Component}
      {slides.current.length > 0 && mediaModalVisibility && (
        <Suspense fallback={<></>}>
          <MediaModal
            slides={slides.current}
            isVisible={mediaModalVisibility}
            onClose={handleModalClose}
            activeMediaIndex={activeMediaIndex}
          />
        </Suspense>
      )}
    </>
  )
}
